import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { AlertCircle, ArrowRight, Calendar, Check, ClipboardList, MapPin, MessageSquare, RotateCcw, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import {
  useCreateBodyPartSuggestionMutation,
  useGetBodyPartSuggestionsQuery,
} from '@/services/api/intakeService';

import {
  QualificationAnswer,
  QualificationOutcome,
  QualificationQuestion,
} from './types';

// ---------------------------------------------------------------------------
// US States for dropdown
// ---------------------------------------------------------------------------

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface QualificationScreenProps {
  questions: QualificationQuestion[];
  answers: Record<string, QualificationAnswer>;
  contextAsked: Record<string, boolean>;
  notes: Record<string, string>;
  outcome: QualificationOutcome;
  incidentDate: string;
  incidentState: string;
  retainerSent: boolean;
  onAnswerChange: (questionId: string, answer: QualificationAnswer) => void;
  onContextAsked: (questionId: string) => void;
  onNoteChange: (questionId: string, note: string) => void;
  onResetQuestion: (questionId: string) => void;
  onIncidentDateChange: (date: string) => void;
  onIncidentStateChange: (state: string) => void;
  injuredBodyParts: string[];
  painLevel: number | null;
  onInjuredBodyPartsChange: (parts: string[]) => void;
  onPainLevelChange: (level: number | null) => void;
  onSendRetainer: () => void;
  onMarkRetainerSentManually: () => void;
  onReject: (reason: string) => void;
  isRetainerSending: boolean;
  /** Field ID to highlight (for deep-link navigation from End Intake) */
  highlightedFieldId?: string | null;
  /** Whether to show the "Continue to Collect" prompt (true when qualified and has collect questions) */
  showCollectPrompt?: boolean;
  /** Callback to navigate to the collect screen */
  onNavigateToCollect?: () => void;
}

// ---------------------------------------------------------------------------
// Helper: Compute qualification outcome
// ---------------------------------------------------------------------------

export function computeQualificationOutcome(
  questions: QualificationQuestion[],
  answers: Record<string, QualificationAnswer> | undefined,
  contextAsked: Record<string, boolean> | undefined
): QualificationOutcome {
  // Safety check for undefined answers/contextAsked
  const safeAnswers = answers ?? {};
  const safeContextAsked = contextAsked ?? {};

  // Check for disqualifying answers
  const hasDisqualifyingAnswer = questions.some((q) => {
    const answer = safeAnswers[q.id];
    if (!q.isDisqualifier || answer === undefined || answer === 'unknown') {
      return false;
    }
    const expectedAnswerStr = q.expectedAnswer ? 'yes' : 'no';
    return answer !== expectedAnswerStr;
  });

  if (hasDisqualifyingAnswer) return 'not_qualified';

  // Check if all questions have definitive answers
  const allAnsweredDefinitively = questions.every((q) => {
    const answer = safeAnswers[q.id];
    return answer === 'yes' || answer === 'no';
  });

  // Check if all context prompts have been asked
  // Skip noteOptional questions with expected answer — they skip the context step entirely
  const allContextAsked = questions.every((q) => {
    if (q.noteOptional && safeAnswers[q.id] === (q.expectedAnswer ? 'yes' : 'no')) return true;
    return safeContextAsked[q.id];
  });

  if (allAnsweredDefinitively && allContextAsked) {
    return 'qualified';
  }

  return 'pending';
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

// Common body part suggestions for search
const BODY_PART_SUGGESTIONS = [
  'Head', 'Neck', 'Shoulder (L)', 'Shoulder (R)', 'Upper Back',
  'Lower Back', 'Chest', 'Ribs', 'Arm (L)', 'Arm (R)',
  'Elbow (L)', 'Elbow (R)', 'Wrist (L)', 'Wrist (R)',
  'Hand (L)', 'Hand (R)', 'Hip (L)', 'Hip (R)',
  'Knee (L)', 'Knee (R)', 'Ankle (L)', 'Ankle (R)',
  'Foot (L)', 'Foot (R)', 'Jaw/TMJ', 'Spine/Cervical',
  'Spine/Thoracic', 'Spine/Lumbar', 'Pelvis', 'Abdomen',
];

interface IncidentContextFieldProps {
  date: string;
  state: string;
  onDateChange: (date: string) => void;
  onStateChange: (state: string) => void;
  /** Whether to show injury details (body parts + pain level) */
  showInjuryDetails?: boolean;
  injuredBodyParts?: string[];
  painLevel?: number | null;
  onInjuredBodyPartsChange?: (parts: string[]) => void;
  onPainLevelChange?: (level: number | null) => void;
}

const IncidentContextField: React.FC<IncidentContextFieldProps> = ({
  date,
  state,
  onDateChange,
  onStateChange,
  showInjuryDetails = false,
  injuredBodyParts = [],
  painLevel = null,
  onInjuredBodyPartsChange,
  onPainLevelChange,
}) => {
  const [tagInput, setTagInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch user-created body part suggestions from the DB
  const { data: dbSuggestions } = useGetBodyPartSuggestionsQuery();
  const [createSuggestion] = useCreateBodyPartSuggestionMutation();

  // Local cache of custom parts added this session (avoids waiting for refetch)
  const [sessionCustomParts, setSessionCustomParts] = useState<string[]>([]);

  const allSuggestions = useMemo(() => {
    const dbNames = dbSuggestions?.map((s) => s.name) ?? [];
    const set = new Set([...BODY_PART_SUGGESTIONS, ...dbNames, ...sessionCustomParts]);
    return [...set];
  }, [dbSuggestions, sessionCustomParts]);

  // Case-insensitive set of selected parts for filtering
  const selectedLower = useMemo(
    () => new Set(injuredBodyParts.map((p) => p.toLowerCase())),
    [injuredBodyParts],
  );

  const filteredSuggestions = useMemo(() => {
    const lower = tagInput.toLowerCase().trim();
    const available = allSuggestions.filter((s) => !selectedLower.has(s.toLowerCase()));
    if (!lower) return available;
    return available.filter((s) => s.toLowerCase().includes(lower));
  }, [tagInput, selectedLower, allSuggestions]);

  // The visible items in the dropdown: suggestions + optional custom "Add" entry
  const visibleSuggestions = filteredSuggestions.slice(0, 12);
  const hasCustomAddOption = !!(
    tagInput.trim() &&
    !selectedLower.has(tagInput.toLowerCase().trim()) &&
    !allSuggestions.some((s) => s.toLowerCase() === tagInput.toLowerCase().trim())
  );
  const totalDropdownItems = visibleSuggestions.length + (hasCustomAddOption ? 1 : 0);
  const showDropdown = isFocused && totalDropdownItems > 0;

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset highlight when suggestions change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [tagInput, filteredSuggestions.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !dropdownRef.current) return;
    const items = dropdownRef.current.querySelectorAll('button');
    items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const addBodyPart = useCallback((part: string) => {
    const trimmed = part.trim();
    if (!trimmed || selectedLower.has(trimmed.toLowerCase())) return;
    // Persist custom parts to DB so other users see them
    if (!BODY_PART_SUGGESTIONS.includes(trimmed)) {
      setSessionCustomParts((prev) => prev.includes(trimmed) ? prev : [...prev, trimmed]);
      createSuggestion({ name: trimmed });
    }
    onInjuredBodyPartsChange?.([...injuredBodyParts, trimmed]);
    setTagInput('');
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }, [injuredBodyParts, selectedLower, onInjuredBodyPartsChange, createSuggestion]);

  const removeBodyPart = (part: string) => {
    onInjuredBodyPartsChange?.(injuredBodyParts.filter((p) => p !== part));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < totalDropdownItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalDropdownItems - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < visibleSuggestions.length) {
        addBodyPart(visibleSuggestions[highlightedIndex]);
      } else if (highlightedIndex === visibleSuggestions.length && hasCustomAddOption) {
        addBodyPart(tagInput.trim());
      } else if (tagInput.trim()) {
        const match = filteredSuggestions.find(
          (s) => s.toLowerCase() === tagInput.toLowerCase().trim(),
        );
        addBodyPart(match || tagInput.trim());
      }
    } else if (e.key === 'Escape') {
      setIsFocused(false);
    }
    if (e.key === 'Backspace' && !tagInput && injuredBodyParts.length > 0) {
      removeBodyPart(injuredBodyParts[injuredBodyParts.length - 1]);
    }
  };

  const painColor = (level: number) => {
    if (level <= 3) return 'bg-emerald-500 text-white';
    if (level <= 6) return 'bg-amber-500 text-white';
    return 'bg-red-500 text-white';
  };

  return (
    <div className="bg-white rounded-[24px] border border-zinc-200 p-5 mb-2 overflow-visible">
      <h3 className="font-bold text-base text-zinc-900 mb-3">
        When and where did the accident happen?
      </h3>
      <div className="flex gap-4">
        {/* Date Picker */}
        <div className="flex-1">
          <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1" id="date-label">
            <Calendar className="w-3 h-3 inline mr-1" />
            Date
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
            aria-labelledby="date-label"
            className="w-full h-11 px-4 rounded-xl border border-zinc-200 bg-zinc-50 text-zinc-900 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 focus:border-transparent transition-colors"
          />
        </div>
        {/* State Dropdown */}
        <div className="flex-1">
          <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1" id="state-label">
            <MapPin className="w-3 h-3 inline mr-1" />
            State
          </div>
          <select
            value={state}
            onChange={(e) => onStateChange(e.target.value)}
            aria-labelledby="state-label"
            className="w-full h-11 px-4 rounded-xl border border-zinc-200 bg-zinc-50 text-zinc-900 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 focus:border-transparent transition-colors appearance-none cursor-pointer"
          >
            <option value="">Select state...</option>
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Injury Details — conditionally shown */}
      <AnimatePresence>
        {showInjuryDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3">
              {/* Body Parts — tag input */}
              <div className="space-y-1.5">
                <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
                  Injured Body Parts
                </div>
                <div className="relative">
                  <div
                    className={cn(
                      'flex flex-wrap items-center gap-1.5 rounded-xl border px-3 py-2 min-h-[44px] cursor-text transition-all',
                      isFocused
                        ? 'border-zinc-900 ring-2 ring-zinc-900/10'
                        : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300',
                    )}
                    onClick={() => inputRef.current?.focus()}
                    role="presentation"
                  >
                    {injuredBodyParts.map((part) => (
                      <motion.span
                        key={part}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                        className="inline-flex items-center gap-1 rounded-full bg-zinc-900 px-2.5 py-0.5 text-[11px] font-bold text-white"
                      >
                        {part}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeBodyPart(part);
                          }}
                          className="rounded-full p-0.5 hover:bg-white/20 active:scale-90 transition-all"
                          aria-label={`Remove ${part}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </motion.span>
                    ))}
                    <input
                      ref={inputRef}
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => {
                        setTimeout(() => setIsFocused(false), 150);
                      }}
                      placeholder={injuredBodyParts.length === 0 ? 'Type to search or add...' : ''}
                      className="flex-1 min-w-[120px] bg-transparent text-sm font-semibold text-zinc-900 outline-none placeholder:text-zinc-400 placeholder:font-normal"
                      aria-label="Add injured body part"
                    />
                  </div>

                  {/* Suggestions dropdown */}
                  {showDropdown && (
                    <div ref={dropdownRef} className="absolute z-20 left-0 right-0 mt-1.5 max-h-48 overflow-y-auto rounded-xl border border-zinc-200 bg-white shadow-xl animate-in fade-in slide-in-from-top-1 duration-150">
                      {visibleSuggestions.map((suggestion, idx) => (
                        <button
                          key={suggestion}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => addBodyPart(suggestion)}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          className={cn(
                            "w-full text-left px-3.5 py-2 text-sm font-semibold transition-colors",
                            highlightedIndex === idx
                              ? "bg-zinc-100 text-zinc-900"
                              : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900",
                          )}
                        >
                          {suggestion}
                        </button>
                      ))}
                      {hasCustomAddOption && (
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => addBodyPart(tagInput.trim())}
                            onMouseEnter={() => setHighlightedIndex(visibleSuggestions.length)}
                            className={cn(
                              "w-full text-left px-3.5 py-2 text-sm font-bold transition-colors border-t border-zinc-100",
                              highlightedIndex === visibleSuggestions.length
                                ? "bg-zinc-100 text-zinc-900"
                                : "text-zinc-900 hover:bg-zinc-50",
                            )}
                          >
                            + Add &quot;{tagInput.trim()}&quot;
                          </button>
                        )}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface QuestionCardProps {
  question: QualificationQuestion;
  answer: QualificationAnswer;
  isContextDone: boolean;
  note: string;
  onAnswer: (answer: QualificationAnswer) => void;
  onContextDone: () => void;
  onNoteChange: (note: string) => void;
  onReset: () => void;
  /** Field ID currently highlighted (e.g., questionId, questionId-note, questionId-context) */
  highlightedFieldId?: string | null;
  /** Order number to display as visual guide (1-based) */
  orderNumber?: number;
}

// Cash App-style animation config - smooth but snappy
const springConfig = { type: 'spring', stiffness: 500, damping: 30 };

// Standard easing for smooth transitions
const standardEase = [0.4, 0, 0.2, 1];

// Warning animations - fade in with subtle scale
const warningVariants = {
  initial: { opacity: 0, scale: 0.97 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: standardEase }
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: { duration: 0.1 }
  },
};

// Badge animations - pop in
const badgeVariants = {
  initial: { opacity: 0, scale: 0.85 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', stiffness: 400, damping: 25 }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: 0.08 }
  },
};

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  answer,
  isContextDone,
  note,
  onAnswer,
  onContextDone,
  onNoteChange,
  onReset,
  highlightedFieldId,
  orderNumber,
}) => {
  const hasAnswer = answer !== undefined;
  const isComplete = hasAnswer && isContextDone;

  // Check what element is highlighted
  const isCardHighlighted = highlightedFieldId === question.id;
  const isNoteHighlighted = highlightedFieldId === `${question.id}-note`;
  const isContextHighlighted = highlightedFieldId === `${question.id}-context`;

  // Check if this answer is disqualifying
  const isDisqualifying = useMemo(() => {
    if (!hasAnswer || answer === 'unknown' || !question.isDisqualifier) {
      return false;
    }
    const expectedAnswerStr = question.expectedAnswer ? 'yes' : 'no';
    return answer !== expectedAnswerStr;
  }, [hasAnswer, answer, question]);

  // Check if answer is "unknown" (needs resolution)
  const isUnknown = answer === 'unknown';

  // Determine the current state for AnimatePresence key
  // For noteOptional questions with the expected answer, skip the note/context step
  const isExpectedAnswer = hasAnswer && answer === (question.expectedAnswer ? 'yes' : 'no');
  const skipNoteStep = question.noteOptional && isExpectedAnswer;

  const getInteractionState = () => {
    if (!hasAnswer) return 'buttons';
    if (skipNoteStep) return 'completed';
    if (!isContextDone) return 'context';
    return 'completed';
  };
  const interactionState = getInteractionState();

  // Determine border styles (no shadows)
  const getBorderStyles = () => {
    if (isCardHighlighted) return 'border-rose-400 ring-2 ring-rose-300 animate-highlight-blink';
    if (isDisqualifying) return 'border-rose-300';
    if (isUnknown) return 'border-amber-300';
    if (interactionState === 'completed' && isExpectedAnswer) return 'border-emerald-300';
    return 'border-zinc-200';
  };

  return (
    <div
      className={cn(
        'bg-white rounded-[20px] border overflow-hidden transition-colors duration-75',
        getBorderStyles(),
      )}
    >
      <div className="p-4 flex flex-col gap-3">
        {/* Question Header */}
        <div className="flex justify-between items-start gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            {/* Order indicator */}
            {orderNumber !== undefined && (
              <motion.div
                className={cn(
                  'shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black transition-colors duration-150',
                  isComplete
                    ? 'bg-emerald-500 text-white'
                    : hasAnswer
                      ? 'bg-zinc-200 text-zinc-500'
                      : 'bg-zinc-900 text-white'
                )}
                animate={{
                  scale: isComplete ? [1, 1.15, 1] : 1,
                }}
                transition={{
                  scale: { duration: 0.2, ease: [0.34, 1.56, 0.64, 1] },
                }}
              >
                {isComplete ? (
                  <Check className="w-3 h-3" />
                ) : (
                  orderNumber
                )}
              </motion.div>
            )}
            <h3
              className={cn(
                'font-bold text-sm leading-tight tracking-tight transition-colors duration-100 pt-0.5',
                hasAnswer ? 'text-zinc-500' : 'text-black'
              )}
            >
              {question.text}
            </h3>
          </div>

          {/* Answer Badge (clickable to reset) */}
          <AnimatePresence mode="wait">
            {hasAnswer && (
              <motion.button
                key="badge"
                type="button"
                onClick={onReset}
                title="Click to change answer"
                className={cn(
                  'group shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1 transition-colors duration-100',
                  answer === 'yes' && 'bg-black text-white hover:bg-zinc-800',
                  answer === 'no' && 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300',
                  answer === 'unknown' && 'bg-amber-100 text-amber-700 hover:bg-amber-200',
                )}
                variants={badgeVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={springConfig}
              >
                {answer === 'yes' && 'YES'}
                {answer === 'no' && 'NO'}
                {answer === 'unknown' && '?'}
                <RotateCcw className="w-2.5 h-2.5 opacity-40 group-hover:opacity-100 transition-opacity" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Interaction Area with smooth height transitions */}
        <motion.div layout transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>
          <AnimatePresence mode="popLayout" initial={false}>
            {interactionState === 'buttons' && (
              <motion.div
                key="buttons"
                layout
                className="flex gap-2"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
              >
                <motion.button
                  type="button"
                  onClick={() => onAnswer('yes')}
                  className="flex-1 h-11 rounded-xl bg-zinc-50 border-2 border-transparent hover:border-black hover:bg-white text-black font-black text-sm tracking-wide transition-colors duration-100"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  transition={springConfig}
                >
                  YES
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => onAnswer('no')}
                  className="flex-1 h-11 rounded-xl bg-zinc-50 border-2 border-transparent hover:border-black hover:bg-white text-black font-black text-sm tracking-wide transition-colors duration-100"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  transition={springConfig}
                >
                  NO
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => onAnswer('unknown')}
                  className="w-14 h-11 rounded-xl bg-zinc-50 border-2 border-transparent hover:border-amber-400 hover:bg-amber-50 text-amber-600 font-black text-sm transition-colors duration-100"
                  title="Unknown / Need more info"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  transition={springConfig}
                >
                  ?
                </motion.button>
              </motion.div>
            )}

            {interactionState === 'context' && (
              <motion.div
                key="context"
                layout
                className="space-y-2"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
              >
                {/* Note Input (required) */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <MessageSquare className="w-3 h-3 text-zinc-400" />
                    <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">
                      Why? <span className="text-rose-400">*</span>
                    </span>
                  </div>
                  <textarea
                    value={note}
                    onChange={(e) => onNoteChange(e.target.value)}
                    placeholder="Explain your answer..."
                    rows={2}
                    className={cn(
                      "w-full px-3 py-2 text-xs font-semibold text-zinc-900 bg-zinc-50 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 focus:border-transparent transition-colors placeholder:text-zinc-400 placeholder:font-normal",
                      isNoteHighlighted
                        ? "border-rose-400 ring-2 ring-rose-300 animate-highlight-blink"
                        : "border-zinc-200"
                    )}
                  />
                </div>

                {/* Context Prompt */}
                <div className="bg-zinc-900 rounded-[16px] py-1 px-4 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1 py-1.5">
                    <div className="text-[8px] font-black text-zinc-500 uppercase tracking-widest mb-0.5">
                      Ask Client
                    </div>
                    <p className="text-white font-semibold text-xs leading-snug">
                      {question.contextPrompt}
                    </p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={onContextDone}
                    disabled={!note.trim()}
                    className={cn(
                      "h-8 px-4 text-[10px] font-black uppercase tracking-wider rounded-[12px] flex items-center gap-1.5 shrink-0 transition-colors duration-100",
                      isContextHighlighted && "ring-2 ring-rose-300 animate-highlight-blink",
                      note.trim()
                        ? "bg-white hover:bg-zinc-200 text-black"
                        : "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                    )}
                    whileHover={note.trim() ? { scale: 1.03 } : {}}
                    whileTap={note.trim() ? { scale: 0.96 } : {}}
                    transition={springConfig}
                  >
                    DONE <Check className="w-3 h-3" />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {interactionState === 'completed' && (
              <motion.div
                key="completed"
                layout
                className="space-y-2"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
              >
                {/* Show saved note */}
                {note && (
                  <div className="bg-zinc-50 rounded-xl px-3 py-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <MessageSquare className="w-2.5 h-2.5 text-zinc-400" />
                      <span className="text-[8px] font-bold text-zinc-400 uppercase tracking-wider">
                        Note
                      </span>
                    </div>
                    <p className="text-xs font-semibold text-zinc-800 leading-snug">{note}</p>
                  </div>
                )}
                {/* Completion indicator */}
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-zinc-100" />
                  <span className="text-[9px] font-bold text-zinc-300 uppercase tracking-widest flex items-center gap-1">
                    <Check className="w-2.5 h-2.5" />
                    Asked
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      // Reset context asked to show the prompt again
                      onContextDone();
                    }}
                    className="text-[9px] font-bold text-zinc-300 hover:text-zinc-500 underline decoration-zinc-200 underline-offset-2 transition-colors duration-150"
                  >
                    Undo
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Disqualification Warning */}
        <AnimatePresence>
          {isDisqualifying && (
            <motion.div
              className="bg-rose-50 text-rose-600 px-3 py-2 rounded-xl flex items-center gap-2 ring-1 ring-rose-200"
              variants={warningVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="font-bold text-xs">Disqualifying Answer</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Unknown Warning */}
        <AnimatePresence>
          {isUnknown && isContextDone && (
            <motion.div
              className="bg-amber-50 text-amber-700 px-3 py-2 rounded-xl flex items-center gap-2"
              variants={warningVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="font-bold text-xs">
                Needs resolution before proceeding
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Continue to Collect Banner
// ---------------------------------------------------------------------------

interface ContinueToCollectBannerProps {
  onContinue: () => void;
}

const ContinueToCollectBanner: React.FC<ContinueToCollectBannerProps> = ({
  onContinue,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.98 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30,
        delay: 0.1
      }}
      className="mt-4"
    >
      <div className="bg-emerald-500 rounded-[20px] p-4 shadow-lg shadow-emerald-500/20">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Check className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">
                Retainer Sent
              </h3>
              <p className="text-emerald-100 text-xs mt-0.5">
                Now collect additional case details
              </p>
            </div>
          </div>
          <motion.button
            type="button"
            onClick={onContinue}
            className="h-10 px-5 bg-white text-emerald-600 font-bold text-sm rounded-xl flex items-center gap-2 hover:bg-emerald-50 transition-colors"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <ClipboardList className="w-4 h-4" />
            Continue to Collect
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const QualificationScreen: React.FC<QualificationScreenProps> = ({
  questions,
  answers,
  contextAsked,
  notes,
  // These props are kept for interface compatibility but handled by parent/footer
  outcome: _outcome,
  incidentDate,
  incidentState,
  retainerSent: _retainerSent,
  onAnswerChange,
  onContextAsked,
  onNoteChange,
  onResetQuestion,
  onIncidentDateChange,
  onIncidentStateChange,
  injuredBodyParts,
  painLevel,
  onInjuredBodyPartsChange,
  onPainLevelChange,
  onSendRetainer: _onSendRetainer,
  onMarkRetainerSentManually: _onMarkRetainerSentManually,
  onReject: _onReject,
  isRetainerSending: _isRetainerSending,
  highlightedFieldId,
  showCollectPrompt = false,
  onNavigateToCollect,
}) => {
  // Safety: ensure answers, contextAsked, and notes are objects
  const safeAnswers = answers ?? {};
  const safeContextAsked = contextAsked ?? {};
  const safeNotes = notes ?? {};

  // Injury details visibility: show when P0 "injured" question is answered "yes",
  // OR when no P0 questions exist (types without qualification), OR when data exists
  const showInjuryDetails = useMemo(() => {
    const hasExistingData = injuredBodyParts.length > 0 || painLevel != null;
    if (hasExistingData) return true;
    // If no qualification questions, always show (e.g., Dog Bite, Misc)
    if (questions.length === 0) return true;
    // Check if any "injured" question is answered "yes"
    const injuryQuestionIds = ['injured', 'pl_injured'];
    return injuryQuestionIds.some((qId) => safeAnswers[qId] === 'yes');
  }, [questions.length, safeAnswers, injuredBodyParts.length, painLevel]);

  // Ref for auto-scrolling when banner appears
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when collect prompt appears
  useEffect(() => {
    if (!showCollectPrompt || !scrollContainerRef.current) {
      return undefined;
    }
    // Small delay to let the banner animate in
    const timer = setTimeout(() => {
      scrollContainerRef.current?.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [showCollectPrompt]);

  // Sort questions by order (if defined), preserving original order for questions without order
  const sortedQuestions = useMemo(() => {
    return [...questions].sort((a, b) => {
      const orderA = a.order ?? Infinity;
      const orderB = b.order ?? Infinity;
      return orderA - orderB;
    });
  }, [questions]);

  // Split into left and right columns (alternating by sorted index)
  const leftColumnQuestions = useMemo(
    () => sortedQuestions.filter((_, i) => i % 2 === 0),
    [sortedQuestions]
  );
  const rightColumnQuestions = useMemo(
    () => sortedQuestions.filter((_, i) => i % 2 === 1),
    [sortedQuestions]
  );

  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden font-sans">
      {/* Questions Grid */}
      <div ref={scrollContainerRef} className="flex-1 p-4 overflow-y-auto flex flex-col">
        <div className="w-full max-w-4xl mx-auto my-auto">
          {/* Incident Context Field */}
          <IncidentContextField
            date={incidentDate}
            state={incidentState}
            onDateChange={onIncidentDateChange}
            onStateChange={onIncidentStateChange}
            showInjuryDetails={showInjuryDetails}
            injuredBodyParts={injuredBodyParts}
            painLevel={painLevel}
            onInjuredBodyPartsChange={onInjuredBodyPartsChange}
            onPainLevelChange={onPainLevelChange}
          />

          {/* Questions in stable 2-column layout (no reordering on height changes) */}
          {sortedQuestions.length === 0 ? (
            <div className="text-center py-8 text-zinc-400">
              <p className="font-semibold">No qualification questions configured</p>
              <p className="text-sm mt-1">Questions will appear here based on case type</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 w-full items-start">
              {/* Left column: even-indexed questions (0, 2, 4, ...) */}
              <div className="flex flex-col gap-2">
                {leftColumnQuestions.map((q) => (
                  <LayoutGroup key={q.id} id={`card-${q.id}`}>
                    <div className="w-full">
                      <QuestionCard
                        question={q}
                        answer={safeAnswers[q.id]}
                        isContextDone={!!safeContextAsked[q.id]}
                        note={safeNotes[q.id] ?? ''}
                        onAnswer={(ans) => onAnswerChange(q.id, ans)}
                        onContextDone={() => onContextAsked(q.id)}
                        onNoteChange={(n) => onNoteChange(q.id, n)}
                        onReset={() => onResetQuestion(q.id)}
                        highlightedFieldId={highlightedFieldId}
                        orderNumber={q.order}
                      />
                    </div>
                  </LayoutGroup>
                ))}
              </div>
              {/* Right column: odd-indexed questions (1, 3, 5, ...) */}
              <div className="flex flex-col gap-2">
                {rightColumnQuestions.map((q) => (
                  <LayoutGroup key={q.id} id={`card-${q.id}`}>
                    <div className="w-full">
                      <QuestionCard
                        question={q}
                        answer={safeAnswers[q.id]}
                        isContextDone={!!safeContextAsked[q.id]}
                        note={safeNotes[q.id] ?? ''}
                        onAnswer={(ans) => onAnswerChange(q.id, ans)}
                        onContextDone={() => onContextAsked(q.id)}
                        onNoteChange={(n) => onNoteChange(q.id, n)}
                        onReset={() => onResetQuestion(q.id)}
                        highlightedFieldId={highlightedFieldId}
                        orderNumber={q.order}
                      />
                    </div>
                  </LayoutGroup>
                ))}
              </div>
            </div>
          )}

          {/* Continue to Collect Banner - shown when qualified and has collect questions */}
          <AnimatePresence>
            {showCollectPrompt && onNavigateToCollect && (
              <ContinueToCollectBanner onContinue={onNavigateToCollect} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default QualificationScreen;
